/* eslint-disable yoda */
import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import {
  DndContext,
  closestCenter,
  useSensors,
  useSensor,
  MouseSensor,
  TouchSensor,
  PointerSensor
} from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy } from '@dnd-kit/sortable';

import { useToast } from '@ps-design-system/components/Toast';

import UploaderMediaButton from './UploaderMediaButton';
import UploaderSortableMediaCard from './UploaderSortableMediaCard';

import useUploaderStore from '../store/useUploaderStore';
import { TOAST_DURATION } from '../constants/actions.consts';
import { reorderAndFormatMedia } from '../utils/dragAndDrop.utils';
import { isEmbedMedia } from '../utils/media.utils';

const UploaderMedia = () => {
  const { toast } = useToast();
  const media = useUploaderStore((state) => state.media);
  const setMedia = useUploaderStore((state) => state.setMedia);
  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor),
    useSensor(PointerSensor, {
      activationConstraint: {
        delay: 250
      }
    }),
  );

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;

    if (!over) {
      return;
    }

    const currentItems = useUploaderStore.getState().media;

    const oldIndex = currentItems.findIndex(item => item.id === active.id);
    const newIndex = currentItems.findIndex(item => item.id === over.id);

    const activeItem = currentItems[oldIndex];

    const isTargetCoverSpot = newIndex === 0;
    const isMediaTypeEmbed = isEmbedMedia(activeItem);

    if (isTargetCoverSpot && isMediaTypeEmbed) {
      toast({
        title: '',
        description: <FormattedMessage id="uploader.toast.error_cover" defaultMessage="Videos and 3D tours cannot be used as cover photos"/>,
        variant: 'destructive',
        duration: TOAST_DURATION
      });

      return;
    }

    if (active.id !== over.id) {
      const newMedia = reorderAndFormatMedia(currentItems, oldIndex, newIndex);
      setMedia(newMedia);
    }
  }, [setMedia, toast]);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={media.map(item => item.id)}
        strategy={rectSortingStrategy}
      >
        <div className='media-container'>
          {media.map((item) => (
            <UploaderSortableMediaCard key={item.id} id={item.id} item={item} />
          ))}
          <UploaderMediaButton />
        </div>
      </SortableContext>
    </DndContext>
  );
};

export default UploaderMedia;
